/**
 * Derived state for the supplier side of an RFQ.
 *
 * Whether a supplier may still edit / submit their quote depends on the
 * RFQ (awarded, cancelled, deadline passed) as well as their own response
 * status. The backend refuses mutations in those states; the UI mirrors
 * the rule so controls are disabled rather than failing on save.
 */
import type { RFQ, SupplierResponse } from '../../lib/api';

type RFQLike = Pick<RFQ, 'status' | 'response_deadline'>;
type ResponseLike = Pick<SupplierResponse, 'response_status'>;

/** RFQ is closed for quoting: awarded or cancelled. */
export function isRfqClosed(rfq: RFQLike): boolean {
  return rfq.status === 'awarded' || rfq.status === 'cancelled';
}

/**
 * The response deadline has passed (compared at end of the deadline day in
 * local time so a same-day deadline is still open).
 */
export function isDeadlinePassed(rfq: RFQLike, now: Date = new Date()): boolean {
  if (!rfq.response_deadline) return false;
  const deadline = new Date(rfq.response_deadline);
  if (Number.isNaN(deadline.getTime())) return false;
  // Date-only deadlines ("2026-09-02") parse as UTC midnight; treat the whole
  // day as open.
  if (/^\d{4}-\d{2}-\d{2}$/.test(rfq.response_deadline)) {
    deadline.setHours(23, 59, 59, 999);
  }
  return now.getTime() > deadline.getTime();
}

/** The supplier's own response can no longer be edited (already submitted / declined / etc.). */
export function isResponseLocked(response: ResponseLike): boolean {
  return response.response_status !== 'pending' && response.response_status !== 'in_progress';
}

/**
 * Whether the supplier may still edit and submit their quote.
 *
 * @returns `{ editable, reason }` — reason is a short human-readable
 *   explanation shown in the form banner when `editable` is false.
 */
export function getResponseEditability(
  rfq: RFQLike,
  response: ResponseLike,
  now: Date = new Date(),
): { editable: boolean; reason: string | null } {
  if (rfq.status === 'awarded') return { editable: false, reason: 'This RFQ has been awarded. Responses are closed.' };
  if (rfq.status === 'cancelled') return { editable: false, reason: 'This RFQ has been cancelled. Responses are closed.' };
  if (isResponseLocked(response)) {
    return { editable: false, reason: `This response has been ${response.response_status.replace('_', ' ')}. Editing is disabled.` };
  }
  if (isDeadlinePassed(rfq, now)) return { editable: false, reason: 'The response deadline has passed. Responses are closed.' };
  return { editable: true, reason: null };
}
